import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { ScreenService } from '@core/services';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { Content, ContentTypeEnum } from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';
import { ContentCardModule } from '../../content-card/content-card.module';
import { HorizontalCardComponent } from '../grid-card-universal/horizontal/horizontal-card.component';
import { VerticalCardComponent } from '../grid-card-universal/vertical/vertical-card.component';
import { VerticalSquareCardComponent } from '../grid-card-universal/vertical-square/vertical-square-card.component';
import { WideHorizontalCardComponent } from '../grid-card-universal/wide-horizontal/wide-horizontal-card.component';
import { WideVerticalCardComponent } from '../grid-card-universal/wide-vertical/wide-vertical-card.component';
import { ExercisesFilterComponent } from './exercises-filter/exercises-filter.component';

@Component({
  selector: 'app-new-grid-exercises',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,
    VerticalCardComponent,
    VerticalSquareCardComponent,
    HorizontalCardComponent,
    WideVerticalCardComponent,
    WideHorizontalCardComponent,
    ModalModule,
    ContentCardModule,
    MobileModalModule,
    ExercisesFilterComponent,
    IconComponent,
  ],
  templateUrl: './new-grid-exercises.component.html',
  styleUrls: ['./new-grid-exercises.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewGridExercisesComponent extends AbstractPane {
  @ViewChild('scroller', { static: false }) scroller: ElementRef;

  @ViewChild('cardHolder', { static: false }) cardHolder: ElementRef;

  public filters: string[];
  public selectedFilter = '0';
  public isDragScrollEnabled = !(this.deviceService.isMobile() || this.deviceService.isTablet());
  public itemSize = 320;
  public reachedPrev = false;
  public reachedNext = false;
  public showModal = false;
  public openedCardIndex: number = null;
  public item: Content;
  public isSmallScreen = true;

  public showCarousel = true;
  public itemsPerRow: number;

  private originalUrl: string;

  protected router = inject(Router);
  protected location = inject(Location);
  protected store = inject(Store);
  protected screenService = inject(ScreenService);

  protected onContentLoaded(viaQuery?: boolean): void {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.isSmallScreen = this.screenService.isSmallScreen;
        this.calcCardInRow(width);
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        this.isSmallScreen = false;
        this.calcCardInRow(window.innerWidth);
      },
    });
    this.filterInit();
    this.isVisible = !!this.contents.length;
  }

  private filterInit() {
    const contentKeys = Object.keys(ContentTypeEnum);
    this.filters = contentKeys
      .slice(0, contentKeys.length / 2)
      .filter((filterItem) => !!this.contents.find((contentItem) => Number(contentItem.type) === Number(filterItem)));
  }

  public selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.markForCheck();
  }

  public filteredContent(): Content[] {
    if (!this.selectedFilter || this.selectedFilter === '0') {
      return (this.pane?.contentItems as Content[]) || this.contents;
    }
    return ((this.pane?.contentItems as Content[]) || this.contents).filter(
      (contentItem) => Number(contentItem.type) === Number(this.selectedFilter),
    );
  }

  moveLeft() {
    this.scroller.nativeElement.scrollBy({
      left: -(this.itemSize + 30),
      behavior: 'smooth',
    });
  }

  moveRight() {
    this.scroller.nativeElement.scrollBy({
      left: this.itemSize + 30,
      behavior: 'smooth',
    });
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.filteredContent().length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
    }
    this.checkReached();
  }

  public onOpenCard(i) {
    // logger.log('ContentDragScrollComponent:onOpenCard', i);
    this.openedCardIndex = i;
    this.item = this.filteredContent()[i];
    this.checkReached();
    this.showModal = !!this.item;

    const url = LibraryRouter.contentRouteUniversal(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      this.originalUrl = currentUrl;
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
  }

  public onCloseCard() {
    // logger.log('ContentDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    this.showModal = false;

    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
    }
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.filteredContent().length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public trackContentByFn(index, item: Content): string {
    return item.id;
  }

  public get defaultItemsWidth() {
    return this.pane?.default_element_width || 320;
  }

  public get maxItemsWidth() {
    return this.pane?.max_element_width || 320;
  }

  public get maxItemsCount() {
    return this.pane?.max_element_count || 10;
  }

  public get content() {
    if (this.pane?.contentItems) {
      return this.pane?.contentItems;
    }
    if (this.userServices?.length) {
      return this.userServices;
    } else if (this.meetings?.length) {
      return this.meetings;
    } else if (this.schools?.length) {
      return this.schools;
    } else if (this.users?.length) {
      return this.users;
    } else if (this.collections?.length) {
      return this.collections;
    } else if (this.contents?.length) {
      return this.contents;
    } else {
      return [];
    }
  }

  public calcCardInRow(width: number) {
    const cardHolderWidth = this.cardHolder?.nativeElement?.clientWidth;
    let maxItemsPerWidth = 0;
    if (!!cardHolderWidth) {
      maxItemsPerWidth = Math.floor(cardHolderWidth / this.defaultItemsWidth);
    } else {
      maxItemsPerWidth = Math.floor((width - 90 - 40) / this.defaultItemsWidth);
    }
    this.itemsPerRow = maxItemsPerWidth;
    this.showCarousel = maxItemsPerWidth < this.contents.length;
    this.detectChanges();
  }

  public get cardType() {
    return this._pane?.new_grid_view || 'vertical';
  }
}
